import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Formik, FormikProps } from 'formik'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import { ToastContainer } from 'react-toastify'
import { showErrorToast, showSuccessToast } from '../../../../shared/components/ToastUtils'
import FormTabHeader from '../../../../shared/components/FormTabHeader'
import { useHistory, useLocation } from 'react-router-dom'
import {
  initialValuesXmlTariffDetail,
  IXmlTariffPriceRow,
  SORT_OPTIONS,
  tabOptionsXmlTariffDetail,
  validationXmlTariffDetail,
  VehicleGroupFilter,
  VEHICLE_GROUP_FILTER_OPTIONS,
  SEGMENT_FILTER_OPTIONS,
} from './XmlTariffDetailModel'
import { currencies, tariffs } from '../../../../shared/models/SharedApiLookups'
import { getXmlTariffPriceEntry, IXmlTariffPriceEntryPayload, saveXmlTariffPriceEntry } from './Api'
import { getXmlTariffById } from '../xml-tariff/Api'
import { APPROVAL_STATUS_OPTIONS, IXmlTariffModel, TYPE_OPTIONS } from '../xml-tariff/XmlTariffModel'
import { loadVehicleGroups } from '../vehicle-group/Api'
import { IVehicleGroupModel } from '../vehicle-group/VehicleGroupModel'
import { getSelectLabel } from '../../../../shared/models/SharedConstantLookups'

function normalizePriceRow(r: Record<string, unknown>, index: number): IXmlTariffPriceRow {
  const id = String(r.vehicleGroupId ?? r.segmentId ?? r.id ?? `row-${index}`)
  return {
    vehicleGroupId: String(r.vehicleGroupId ?? ''),
    vehicleGroupName: String(r.vehicleGroupName ?? ''),
    segmentId: String(r.segmentId ?? ''),
    segmentName: String(r.segmentName ?? ''),
    dayOnePrice: Number(r.dayOnePrice ?? 0),
    dayTwoPrice: Number(r.dayTwoPrice ?? 0),
    dayThreePrice: Number(r.dayThreePrice ?? 0),
    dayFourPrice: Number(r.dayFourPrice ?? 0),
    dayFivePrice: Number(r.dayFivePrice ?? 0),
    daySixPrice: Number(r.daySixPrice ?? 0),
    daySevenPrice: Number(r.daySevenPrice ?? 0),
    monthlyPrice: Number(r.monthlyPrice ?? 0),
    currencyId: String(r.currencyId ?? ''),
  }
}

function parsePriceEntryPayload(payload: unknown): {
  rows: IXmlTariffPriceRow[]
  headerList: string[]
} {
  if (payload && typeof payload === 'object') {
    const p = payload as IXmlTariffPriceEntryPayload
    const rawList = p.xmlRatePriceList || []
    const rows = rawList.map((x, i) =>
      normalizePriceRow(x as unknown as Record<string, unknown>, i)
    )
    const headerList = Array.isArray(p.headerList) && p.headerList.length > 0
      ? p.headerList
      : DEFAULT_HEADERS
    return { rows, headerList }
  }
  if (Array.isArray(payload)) {
    return {
      rows: payload.map((x, i) => normalizePriceRow(x as Record<string, unknown>, i)),
      headerList: DEFAULT_HEADERS,
    }
  }
  return { rows: [], headerList: DEFAULT_HEADERS }
}

function vehicleGroupToRow(g: IVehicleGroupModel, defaultCurrencyId: string, index: number): IXmlTariffPriceRow {
  const id = g.id || `vg-${index}`
  return {
    vehicleGroupId: id,
    vehicleGroupName: g.groupName,
    segmentId: '',
    segmentName: '',
    dayOnePrice: 0,
    dayTwoPrice: 0,
    dayThreePrice: 0,
    dayFourPrice: 0,
    dayFivePrice: 0,
    daySixPrice: 0,
    daySevenPrice: 0,
    monthlyPrice: 0,
    currencyId: defaultCurrencyId,
  }
}

function applyVehicleGroupFilter(groups: IVehicleGroupModel[], filter: VehicleGroupFilter): IVehicleGroupModel[] {
  if (filter === 'ALL') {
    return groups
  }
  if (filter === 'ACTIVE_GROUPS') {
    return groups.filter((g) => !g.isPassive)
  }
  const parked = groups.filter((g) => (g as IVehicleGroupModel & { inPark?: boolean }).inPark === true)
  if (parked.length > 0) {
    return parked
  }
  return []
}

function formatDateDisplay(iso?: string): string {
  if (!iso) {
    return '-'
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso.split('T')[0]?.split('-').reverse().join('.') || iso
  }
  return d.toLocaleDateString('tr-TR')
}

function formatTariffDate(v: Date | string | undefined): string {
  if (v == null) return '-'
  const d = v instanceof Date ? v : new Date(v)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('tr-TR')
}

function typeLabel(code?: string): string {
  if (!code) return '-'
  const o = TYPE_OPTIONS.find((x) => x.value === code)
  return o?.label ?? code
}

const PRICE_FIELDS = [
  'dayOnePrice',
  'dayTwoPrice',
  'dayThreePrice',
  'dayFourPrice',
  'dayFivePrice',
  'daySixPrice',
  'daySevenPrice',
  'monthlyPrice',
] as const

const DEFAULT_HEADERS = ['1', '2', '3', '4', '5', '6', '7', 'Diğer', 'Aylık']

export const XmlTariffDetailPage: FC = () => {
  const [tab, setTab] = useState<'form' | 'list'>('form')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const history = useHistory()
  const location = useLocation()
  const formikRef = useRef<FormikProps<Record<string, string>>>(null)

  const tariffIdFromUrl = useMemo(() => {
    const q = new URLSearchParams(location.search)
    return q.get('tariffId')?.trim() || ''
  }, [location.search])

  const [selectedTariffId, setSelectedTariffId] = useState(tariffIdFromUrl)
  const [vehicleGroupFilter, setVehicleGroupFilter] = useState<VehicleGroupFilter>('WITH_PRICE_GROUPS')
  const [sortKey, setSortKey] = useState<(typeof SORT_OPTIONS)[number]['value']>('name')
  const [reflectCurrencyId, setReflectCurrencyId] = useState('')
  const [rows, setRows] = useState<IXmlTariffPriceRow[]>([])
  const [headerList, setHeaderList] = useState<string[]>(DEFAULT_HEADERS)
  const [headerRateName, setHeaderRateName] = useState('')
  const [headerStartDate, setHeaderStartDate] = useState('')
  const [headerEndDate, setHeaderEndDate] = useState('')

  const [tariffOptions, setTariffOptions] = useState<{ value: string; label: string }[]>([])
  const [currencyOptions, setCurrencyOptions] = useState<{ value: string; label: string }[]>([])
  const [filterOpen, setFilterOpen] = useState(true)
  const [tariffInfoOpen, setTariffInfoOpen] = useState(false)
  const [listFilterOpen, setListFilterOpen] = useState(true)
  const [tariffDetail, setTariffDetail] = useState<IXmlTariffModel | null>(null)
  const [tariffDetailLoading, setTariffDetailLoading] = useState(false)

  useEffect(() => {
    setSelectedTariffId(tariffIdFromUrl)
  }, [tariffIdFromUrl])

  useEffect(() => {
    if (!tariffIdFromUrl) {
      setTariffDetail(null)
      return
    }
    let cancelled = false
    setTariffDetailLoading(true)
    getXmlTariffById(tariffIdFromUrl)
      .then((res) => {
        if (cancelled) return
        if (res.data.hasError) {
          showErrorToast(res.data.message || 'Tarife bilgisi alınamadı')
          setTariffDetail(null)
          return
        }
        setTariffDetail(res.data.payload ?? null)
      })
      .catch(() => {
        if (!cancelled) {
          showErrorToast('Tarife bilgisi yüklenemedi')
          setTariffDetail(null)
        }
      })
      .finally(() => {
        if (!cancelled) setTariffDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [tariffIdFromUrl])

  useEffect(() => {
    let cancelled = false
      ; (async () => {
        try {
          const [cur, trf] = await Promise.all([currencies(), tariffs()])
          if (cancelled) {
            return
          }
          setCurrencyOptions(cur)
          setTariffOptions(trf)
        } catch {
          if (!cancelled) {
            showErrorToast('Liste verileri yüklenemedi')
          }
        }
      })()
    return () => {
      cancelled = true
    }
  }, [])

  const defaultCurrencyId = currencyOptions[0]?.value || ''

  const loadRowsFromVehicleGroupsFallback = useCallback(
    async (filter: VehicleGroupFilter, currencyDefault: string) => {
      const vgRes = await loadVehicleGroups(0, 1000, 'groupName', 'asc')
      if (vgRes.data.hasError) {
        throw new Error(vgRes.data.message || 'Araç grupları alınamadı')
      }
      let list = vgRes.data.payload.content
      list = applyVehicleGroupFilter(list, filter)
      setRows(list.map((g, i) => vehicleGroupToRow(g, currencyDefault, i)))
      setHeaderRateName('')
      setHeaderStartDate('')
      setHeaderEndDate('')
      if (filter === 'PARK_GROUPS' && list.length === 0) {
        showErrorToast('Parkta araç grubu bulunamadı (inPark alanı veya servis yanıtı gerekli)')
      }
    },
    []
  )

  const loadRowsFromApi = useCallback(
    async (tariffId: string | undefined, filter: VehicleGroupFilter) => {
      setTableLoading(true)
      const currencyDefault = defaultCurrencyId || ''
      try {
        const res = await getXmlTariffPriceEntry(tariffId || null, filter)
        if (res.data.hasError) {
          throw new Error(res.data.message || 'Tarife fiyat verisi alınamadı')
        }
        const parsed = parsePriceEntryPayload(res.data.payload)
        const next = parsed.rows.map((row) => ({
          ...row,
          currencyId: row.currencyId || currencyDefault,
        }))
        setHeaderList(parsed.headerList)
        if (next.length > 0) {
          setRows(next)
          console.log('API verisi kullanıldı:', next)
          return
        }
        if (tariffId) {
          setRows([])
          return
        }
        await loadRowsFromVehicleGroupsFallback(filter, currencyDefault)
        showErrorToast('Servis boş döndü; araç gruplarından oluşturuldu')
      } catch (e) {
        if (tariffId) {
          setRows([])
          showErrorToast(e instanceof Error ? e.message : 'Tarife verisi yüklenemedi')
          return
        }
        try {
          await loadRowsFromVehicleGroupsFallback(filter, currencyDefault)
          showErrorToast('Tarife servisine ulaşılamadı; araç grupları kullanıldı')
        } catch (e2) {
          setRows([])
          showErrorToast(e2 instanceof Error ? e2.message : 'Tablo verisi yüklenemedi')
        }
      } finally {
        setTableLoading(false)
      }
    },
    [defaultCurrencyId, loadRowsFromVehicleGroupsFallback]
  )

  useEffect(() => {
    void loadRowsFromApi(selectedTariffId || undefined, vehicleGroupFilter)
  }, [selectedTariffId, vehicleGroupFilter, loadRowsFromApi])

  const sortedRows = useMemo(() => {
    const copy = [...rows]
    const cmp = (a: string, b: string) => a.localeCompare(b, 'tr', { sensitivity: 'base' })
    if (sortKey === 'name') {
      copy.sort((a, b) => cmp(a.vehicleGroupName, b.vehicleGroupName))
    } else if (sortKey === 'segment') {
      copy.sort((a, b) => cmp(a.segmentName, b.segmentName))
    }
    console.log('Sıralanmış satırlar:', copy)

    // sipp ve segment alanları servisten gelmiyor, sadece isme göre sırala
    return copy
  }, [rows, sortKey])

  const rowFormikInitialValues = useMemo(() => {
    const vals: Record<string, string> = { ...(initialValuesXmlTariffDetail as unknown as Record<string, string>) }
    vals.selectedTariffId = selectedTariffId
    vals.vehicleGroupFilter = vehicleGroupFilter
    vals.reflectCurrencyId = reflectCurrencyId
    for (const row of sortedRows) {
      const id = row.vehicleGroupId === '' ? row.segmentId : row.vehicleGroupId
      for (const f of PRICE_FIELDS) {
        vals[`row_${id}_${f}`] = row[f] != null ? String(row[f]) : '0'
      }
      vals[`row_${id}_currencyId`] = row.currencyId || defaultCurrencyId
    }
    return vals
  }, [sortedRows, selectedTariffId, vehicleGroupFilter, reflectCurrencyId, defaultCurrencyId])

  const goToTariff = () => {
    if (!selectedTariffId) {
      showErrorToast('Lütfen bir tarife seçin')
      return
    }
    history.push(`/definition/xml-tariff-detail?tariffId=${encodeURIComponent(selectedTariffId)}`)
  }

  const onTariffSelect = (id: string) => {
    setSelectedTariffId(id)
    if (id) {
      history.replace(`/definition/xml-tariff-detail?tariffId=${encodeURIComponent(id)}`)
    } else {
      history.replace('/definition/xml-tariff-detail')
    }
  }

  const onReflectCurrency = (currencyId: string) => {
    setReflectCurrencyId(currencyId)
    if (!currencyId) {
      return
    }
    setRows((prev) => prev.map((r) => ({ ...r, currencyId })))
  }

  const updateRow = (rowId: string, patch: Partial<IXmlTariffPriceRow>) => {
    setRows((prev) => prev.map((r) => (r.vehicleGroupId === rowId || r.segmentId === rowId ? { ...r, ...patch } : r)))
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      const formValues = formikRef.current?.values || {}
      const updatedRows = rows.map((r) => {
        const updated = { ...r }
        for (const f of PRICE_FIELDS) {
          const key = `row_${r.vehicleGroupId === '' ? r.segmentId : r.vehicleGroupId}_${f}`
          if (key in formValues) {
            updated[f] = Number(String(formValues[key]).replace(/[^0-9.,\-]/g, '').replace(',', '.')) || 0
          }
        }
        return updated
      })
      const filteredRows = updatedRows.filter(
        (r) =>
          r.dayOnePrice > 0 ||
          r.dayTwoPrice > 0 ||
          r.dayThreePrice > 0 ||
          r.dayFourPrice > 0 ||
          r.dayFivePrice > 0 ||
          r.daySixPrice > 0 ||
          r.daySevenPrice > 0 ||
          r.monthlyPrice > 0
      )
      const res = await saveXmlTariffPriceEntry({
        xmlRateId: selectedTariffId || null,
        xmlRatePriceList: filteredRows,
      })
      if (res.data.hasError) {
        showErrorToast(res.data.message || 'Kayıt sırasında hata oluştu')
        return
      }
      setVehicleGroupFilter('WITH_PRICE_GROUPS') 
      showSuccessToast('Kayıt başarıyla kaydedildi')
    } catch {
      showErrorToast('Kayıt sırasında hata oluştu')
    } finally {
      setIsSubmitting(false)
    }
  }

  const dateRangeText =
    headerStartDate && headerEndDate
      ? `${formatDateDisplay(headerStartDate)} - ${formatDateDisplay(headerEndDate)}`
      : '-'

  const selectedTariffLabel =
    tariffOptions.find((t) => t.value === selectedTariffId)?.label || headerRateName || '-'

  const reservationSourceLabels = (() => {
    const rs = tariffDetail?.reservationSource
    if (!rs || !Array.isArray(rs) || rs.length === 0) return '-'
    return rs
      .map((reservation) => reservation.reservationSourceName ?? reservation.id)
      .join(', ')
  })()

  return (
    <div className='container-fluid'>
      <ToastContainer />

      <FormTabHeader
        formId='xml-tariff-detail-form'
        isVisibleResetButton={false}
        formikSubmit={() => void handleSubmit()}
        formikReset={() => formikRef.current?.resetForm()}
        loading={isSubmitting}
        tab={tab}
        setTab={(key: string) => setTab(key as 'form' | 'list')}
        setSelectedId={() => { }}
        tabs={tabOptionsXmlTariffDetail}
        onExportExcel={() => console.log('Excel export')}
      />

      {tab === 'form' && (
        <div className='card'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title'>
              <div className='d-flex align-items-center gap-2 my-1'>
                <i className='bi bi-funnel fs-1 text-primary'></i>
                <h3 className='fw-bold m-0'>Tarife Filtreleri</h3>
              </div>
            </div>
            <div className='card-toolbar'>
              <button
                type='button'
                className='btn btn-sm btn-light-primary me-3'
                onClick={() => {
                  const id = selectedTariffId || formikRef.current?.values.id
                  history.push(
                    id
                      ? `/definition/xml-tariff?tariffId=${encodeURIComponent(id)}`
                      : '/definition/xml-tariff'
                  )
                }}
              >
                <i className='bi bi-arrow-left me-1'></i>
                Tarifeye Geri Dön
              </button>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => setFilterOpen((prev) => !prev)}
              >
                <i className={`ki-duotone ki-${filterOpen ? 'up' : 'down'} fs-2`}>
                  <span className='path1'></span>
                  <span className='path2'></span>
                </i>
                {filterOpen ? 'Gizle' : 'Göster'}
              </button>
            </div>
          </div>

          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={rowFormikInitialValues}
            validationSchema={validationXmlTariffDetail}
            onSubmit={() => void handleSubmit()}
          >
            {(formik) => (
              <Form id='xml-tariff-detail-form'>
                <div className={`collapse ${filterOpen ? 'show' : ''}`}>
                  <div className='card-body border-top pt-6'>
                    <div className='row g-6 mb-6 align-items-end'>
                      <div className='col-md-6'>
                        <SelectInput
                          name='selectedTariffId'
                          label='Seçili Tarife'
                          options={tariffOptions}
                          firstItemLabel='Tarife seçiniz'
                          onChange={(v) => onTariffSelect(String(v ?? ''))}
                        />
                      </div>
                      <div className='col-md-3'>
                        <SelectInput
                          name='vehicleGroupFilter'
                          label='Araç grubu listesi'
                          options={tariffDetail?.tariffSubType === 'SEGMENT' ? SEGMENT_FILTER_OPTIONS : VEHICLE_GROUP_FILTER_OPTIONS}
                          onChange={(v) => setVehicleGroupFilter((v as VehicleGroupFilter) || 'WITH_PRICE_GROUPS')}
                        />
                      </div>
                      <div className='col-md-3'>
                        <SelectInput
                          name='reflectCurrencyId'
                          label='Aşağıya yansıt (döviz)'
                          options={currencyOptions}
                          defaultValue={defaultCurrencyId} 
                          onChange={(v) => onReflectCurrency(String(v ?? ''))}
                        />
                      </div>
                      <div className='col-md-6'>
                        <label className='form-label fw-bold'>Tarih Aralığı</label>
                        <div className='d-flex align-items-center gap-2 p-3 bg-light rounded border'>
                          {tariffDetailLoading && (
                            <>
                              <span className='spinner-border spinner-border-sm text-primary' role='status' />
                              <span>Tarife bilgileri yükleniyor…</span>
                            </>
                          )}
                          {!tariffDetailLoading && tariffDetail && (
                            <>
                              <span className='fw-semibold'>{formatTariffDate(tariffDetail.startDate)} - {formatTariffDate(tariffDetail.endDate)}</span>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon btn-light-primary ms-auto'
                                onClick={() => setTariffInfoOpen((prev) => !prev)}
                                title={tariffInfoOpen ? 'Detayı Gizle' : 'Detayı Göster'}
                              >
                                <i className={`bi bi-chevron-${tariffInfoOpen ? 'up' : 'down'} fs-4`}></i>
                              </button>
                            </>
                          )}
                          {!tariffDetailLoading && !tariffDetail && (
                            <span className='text-muted'>-</span>
                          )}
                        </div>
                      </div> 
                      {/*<div className='col-md-3'>
                  <label className='form-label fw-bold'>Sıralama</label>
                  <div className='input-group'>
                    <span className='input-group-text'>Sırala</span>
                    <select
                      className='form-select'
                      value={sortKey}
                      onChange={(e) => setSortKey(e.target.value as typeof sortKey)}
                    >
                      {SORT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>*/}
                    </div>
                    <div className={`collapse ${tariffInfoOpen ? 'show' : ''}`}>
                      <div className='row g-6 mb-0'>
                        <div className='col-12'>
                          <div className='p-4 bg-light rounded border'>
                            {tariffDetailLoading && (
                              <div className='text-center py-2'>
                                <span className='spinner-border spinner-border-sm text-primary me-2' role='status' />
                                Tarife bilgileri yükleniyor…
                              </div>
                            )}
                            {!tariffDetailLoading && tariffDetail && (
                              <div className='row g-4'>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Tarife adı</span>
                                  <span className='fw-semibold'>{tariffDetail.rateName || '-'}</span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Tür</span>
                                  <span className='fw-semibold'>{typeLabel(tariffDetail.tariffType)}</span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Durum</span>
                                  <span className='fw-semibold'>
                                    {tariffDetail.status === undefined ? '-' : tariffDetail.status ? 'Aktif' : 'Pasif'}
                                  </span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Onay durumu</span>
                                  <span className='fw-semibold'>{getSelectLabel(APPROVAL_STATUS_OPTIONS, tariffDetail.approvalStatus)}</span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>İşlem tarihi</span>
                                  <span className='fw-semibold'>{formatTariffDate(tariffDetail.actionDate)}</span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Başlangıç</span>
                                  <span className='fw-semibold'>{formatTariffDate(tariffDetail.startDate)}</span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Bitiş</span>
                                  <span className='fw-semibold'>{formatTariffDate(tariffDetail.endDate)}</span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Max kira tarihi</span>
                                  <span className='fw-semibold'>{formatTariffDate(tariffDetail.maxRentalDate)}</span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Rezervasyon kaynakları</span>
                                  <span className='fw-semibold text-break'>{reservationSourceLabels}</span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Şubeler</span>
                                  <span className='fw-semibold text-break'>
                                    {Array.isArray(tariffDetail.actionBranch)
                                      ? tariffDetail.actionBranch.length
                                        ? tariffDetail.actionBranch.map((b: any) => typeof b === 'object' ? (b.branchName || b.id || '-') : b).join(', ')
                                        : '-'
                                      : typeof tariffDetail.actionBranch === 'object' && tariffDetail.actionBranch
                                        ? (tariffDetail.actionBranch as any).branchName || '-'
                                        : tariffDetail.actionBranch || '-'}
                                  </span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Özel lokasyon</span>
                                  <span className='fw-semibold text-break'>
                                    {Array.isArray(tariffDetail.specialLocation) && tariffDetail.specialLocation.length > 0
                                      ? tariffDetail.specialLocation.map((loc: any) => loc.name || loc.id || '-').join(', ')
                                      : '-'}
                                  </span>
                                </div>
                                <div className='col-sm-6 col-lg-3'>
                                  <span className='text-muted fs-7 d-block'>Karşılaştırma indirim %</span>
                                  <span className='fw-semibold'>
                                    {tariffDetail.comparisonSystemDiscountRate ?? '-'}
                                  </span>
                                </div>
                              </div>
                            )}
                            {!tariffDetailLoading && !tariffDetail && tariffIdFromUrl && (
                              <p className='text-muted mb-0 small'>Bu tarife ait kayıt API üzerinden bulunamadı.</p>
                            )}
                            {!tariffDetailLoading && !tariffIdFromUrl && (
                              <div className='row g-3'>
                                <div className='col-md-6'>
                                  <span className='text-muted fs-7 d-block'>Liste (fiyat girişi)</span>
                                  <span className='fw-semibold'>{selectedTariffLabel}</span>
                                </div>
                                <div className='col-md-6'>
                                  <span className='text-muted fs-7 d-block'>Tarih aralığı (tablo)</span>
                                  <span className='fw-semibold'>{dateRangeText}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='card-body pt-0'>
                  <div className='table-responsive'>
                    <table className='table table-bordered table-hover align-middle mb-0 small'>
                      <thead className='table-light'>
                        <tr>
                          <th style={{ minWidth: '220px' }}>{vehicleGroupFilter === 'SEGMENT_GROUPS' ? 'Segment' : 'Araç Grubu'}</th>
                          {headerList.map((h, i) => (
                            <th key={i}>{h}</th>
                          ))}
                          <th style={{ minWidth: '100px' }}>Döviz</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableLoading ? (
                          <tr>
                            <td colSpan={headerList.length + 2} className='text-center py-5'>
                              <span className='spinner-border spinner-border-sm text-primary me-2' />
                              Yükleniyor…
                            </td>
                          </tr>
                        ) : sortedRows.length === 0 ? (
                          <tr>
                            <td colSpan={headerList.length + 2} className='text-center text-muted py-4'>
                              Kayıt yok
                            </td>
                          </tr>
                        ) : (
                          sortedRows.map((row) => (
                            <tr key={row.vehicleGroupId === '' ? row.segmentId : row.vehicleGroupId}>
                              <td className='text-break'>{row.vehicleGroupName === '' ? row.segmentName : row.vehicleGroupName}</td>
                              {PRICE_FIELDS.map((field) => (
                                <td key={field} style={{ width: '88px' }}>
                                  <TextInput
                                    name={`row_${row.vehicleGroupId === '' ? row.segmentId : row.vehicleGroupId}_${field}`}
                                    currency
                                    className='form-control-sm'
                                  />
                                </td>
                              ))}
                              <td>
                                <SelectInput
                                  name={`row_${row.vehicleGroupId === '' ? row.segmentId : row.vehicleGroupId}_currencyId`}
                                  options={currencyOptions}
                                  firstItemLabel='Seçiniz'
                                  className='form-select form-select-sm'
                                  onChange={(v) =>
                                    updateRow(row.vehicleGroupId === '' ? row.segmentId : row.vehicleGroupId, { currencyId: String(v ?? '') })
                                  }
                                />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {tab === 'list' && (
        <div className='card'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title'>
              <div className='d-flex align-items-center gap-2 my-1'>
                <i className='bi bi-funnel fs-1 text-primary'></i>
                <h3 className='fw-bold m-0'>Xml Fiyat Aktarımı</h3>
              </div>
            </div>
            <div className='card-toolbar'>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => setListFilterOpen((prev) => !prev)}
              >
                <i className={`ki-duotone ki-${listFilterOpen ? 'up' : 'down'} fs-2`}>
                  <span className='path1'></span>
                  <span className='path2'></span>
                </i>
                {listFilterOpen ? 'Gizle' : 'Göster'}
              </button>
            </div>
          </div>
          <div className={`collapse ${listFilterOpen ? 'show' : ''}`}>
            <div className='card-body border-top pt-6'>
              <div className='row g-6 mb-6'>
                <div className='col-md-6'>
                  <label className='form-label fw-bold'>Kaynak Tarife Adı</label>
                  <select className='form-select'>
                    <option>Tarife seçiniz</option>
                    {tariffOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-md-6'>
                  <label className='form-label fw-bold'>Aktarım Oranı (* 1,2 = %20 artırır)</label>
                  <div className='input-group'>
                    <input type='number' className='form-control' defaultValue={1} step={0.01} />
                    <button type='button' className='btn btn-primary'>
                      Fiyatları Aktar
                    </button>
                  </div>
                </div>
              </div>
              <div className='row g-6 mb-0 align-items-center'>
                <div className='col-md-6'>
                  <input
                    type='text'
                    className='form-control'
                    readOnly
                    placeholder='Dosya Seçiniz (*.xlsx)'
                  />
                </div>
                <div className='col-md-6 d-flex flex-wrap gap-2'>
                  <button type='button' className='btn btn-secondary btn-sm'>
                    Dosya Seçiniz
                  </button>
                  <button type='button' className='btn btn-light border btn-sm'>
                    Görüntüle
                  </button>
                  <button type='button' className='btn btn-light border btn-sm'>
                    Fiyat Tablosunu Aktar
                  </button>
                  <button type='button' className='btn btn-light border btn-sm'>
                    Temel Fiyat Olarak Kaydet
                  </button>
                  <button type='button' className='btn btn-light border btn-sm'>
                    Temel Liste
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
